import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { EnvelopeAtIcon, FacebookIcon, PencilFillIcon, Trash3FillIcon } from "../components/icons";
import { formatEventDate } from "../lib/formatDate";
import { RichTextEditor } from "../components/RichTextEditor";
import Toast from "../components/Toast";
import ClubsAdminSection from "../components/ClubsAdminSection";
import { cleanEventForm, EventForm, useClassOptions } from "../components/EventForm";
import {
  awardCredit,
  createAdminArticle,
  createAdminEvent,
  createAdminLocation,
  createAdminRegistrationCheckout,
  createHeat,
  createUserVehicle,
  deleteAdminArticle,
  deleteAdminEvent,
  deleteAdminLocation,
  deleteFieldDefinition,
  deleteHeat,
  deleteUserVehicle,
  getLocationPhotoUploadUrl,
  listAdminArticles,
  listAdminClubs,
  listAdminEvents,
  listAdminLocations,
  listAdminUsers,
  listEventRegistrations,
  listFieldDefinitions,
  listHeats,
  listUserVehicles,
  updateAdminArticle,
  updateAdminEvent,
  updateAdminLocation,
  updateAdminUser,
  updateHeatResults,
  updateRegistrationStatus,
  updateUserVehicle,
  upsertFieldDefinition,
  type ArticleRow,
  type ClubRow,
  type EventRow,
  type FieldAppliesTo,
  type FieldDefinitionRow,
  type HeatResult,
  type HeatRoundType,
  type HeatRow,
  type LocationRow,
  type LocationType,
  type RegistrationRow,
  type UserRow,
  type VehicleRow,
} from "../lib/api";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Lets an admin edit or remove another member's cars from the Users panel --
 *  same vehicle shape as the member's own "My cars" tab, just scoped by
 *  userId instead of the caller's own sub. */
const EMPTY_VEHICLE_FORM: Partial<VehicleRow> = { manufacturer: "", class: "", radioManufacturer: "", transponderNumber: "", customFields: {} };

function UserVehiclesPanel({ userId, racerName }: { userId: string; racerName: string }) {
  const [vehicles, setVehicles] = useState<VehicleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Partial<VehicleRow>>({});

  const [publishedEvents, setPublishedEvents] = useState<EventRow[]>([]);
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const [regEventId, setRegEventId] = useState("");
  const [regClass, setRegClass] = useState("");
  const [regError, setRegError] = useState<string | null>(null);
  const [regResult, setRegResult] = useState<string | null>(null);
  const [fieldDefs, setFieldDefs] = useState<FieldDefinitionRow[]>([]);

  function refresh() {
    listUserVehicles(userId)
      .then((res) => setVehicles(res.vehicles))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load vehicles."));
  }
  useEffect(refresh, [userId]);
  useEffect(() => {
    listAdminEvents()
      .then((res) => setPublishedEvents(res.events.filter((e) => e.status === "published")))
      .catch(() => {});
  }, []);
  useEffect(() => {
    listFieldDefinitions("vehicle").then((res) => setFieldDefs(res.fields)).catch(() => {});
  }, []);

  function startRegister(v: VehicleRow) {
    setRegisteringId(v.vehicleId);
    setRegEventId(publishedEvents[0]?.eventId ?? "");
    setRegClass(v.class);
    setRegError(null);
    setRegResult(null);
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!regEventId) return;
    setRegError(null);
    setRegResult(null);
    try {
      const res = await createAdminRegistrationCheckout(regEventId, {
        userId,
        racerName,
        racerClass: regClass,
      });
      setRegResult(
        res.freeCheckout ? "Registered -- no payment needed for this event." : (res.checkoutUrl ?? null),
      );
    } catch (err) {
      // Stripe unavailable -> 503 "Payment provider unavailable, ...": the
      // registration is saved as pending; trying again reuses it.
      setRegError(err instanceof Error ? err.message : "Registration failed.");
    }
  }

  function startEdit(v: VehicleRow) {
    setAdding(false);
    setEditingId(v.vehicleId);
    setForm({
      manufacturer: v.manufacturer,
      class: v.class,
      radioManufacturer: v.radioManufacturer,
      transponderNumber: v.transponderNumber,
      customFields: v.customFields ?? {},
    });
  }

  function startAdd() {
    setEditingId(null);
    setForm(EMPTY_VEHICLE_FORM);
    setAdding(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (adding) {
        await createUserVehicle(userId, {
          manufacturer: form.manufacturer ?? "",
          class: form.class ?? "",
          radioManufacturer: form.radioManufacturer,
          transponderNumber: form.transponderNumber,
          customFields: form.customFields,
        });
        setAdding(false);
      } else if (editingId) {
        await updateUserVehicle(userId, editingId, form);
        setEditingId(null);
      }
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  async function handleDelete(vehicleId: string) {
    if (!confirm("Remove this vehicle?")) return;
    try {
      await deleteUserVehicle(userId, vehicleId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  const vehicleForm = (onCancel: () => void) => (
    <form onSubmit={handleSave} style={{ marginBottom: "0.5rem" }}>
      <div className="row">
        <input value={form.manufacturer ?? ""} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} placeholder="Manufacturer" required />
        <input value={form.class ?? ""} onChange={(e) => setForm({ ...form, class: e.target.value })} placeholder="Class" required />
        <input value={form.radioManufacturer ?? ""} onChange={(e) => setForm({ ...form, radioManufacturer: e.target.value })} placeholder="Radio" />
        <input value={form.transponderNumber ?? ""} onChange={(e) => setForm({ ...form, transponderNumber: e.target.value })} placeholder="Transponder #" />
      </div>
      {fieldDefs.length > 0 && (
        <div className="row" style={{ marginTop: "0.5rem" }}>
          {fieldDefs.map((f) =>
            f.type === "boolean" ? (
              <label key={f.fieldKey} style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={!!form.customFields?.[f.fieldKey]}
                  onChange={(e) => setForm({ ...form, customFields: { ...form.customFields, [f.fieldKey]: e.target.checked } })}
                />
                {f.label}
              </label>
            ) : f.type === "select" ? (
              <select
                key={f.fieldKey}
                value={String(form.customFields?.[f.fieldKey] ?? "")}
                onChange={(e) => setForm({ ...form, customFields: { ...form.customFields, [f.fieldKey]: e.target.value } })}
              >
                <option value="" disabled>{f.label}</option>
                {(f.options ?? []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                key={f.fieldKey}
                type={f.type === "number" ? "number" : "text"}
                value={String(form.customFields?.[f.fieldKey] ?? "")}
                onChange={(e) => setForm({ ...form, customFields: { ...form.customFields, [f.fieldKey]: e.target.value } })}
                placeholder={f.label}
              />
            ),
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button type="submit" style={{ flex: "0 0 auto" }}>Save</button>
        <button type="button" className="secondary" style={{ flex: "0 0 auto" }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <h4>Vehicles</h4>
      {error && <p className="err">{error}</p>}
      {vehicles?.map((v) =>
        editingId === v.vehicleId ? (
          <div key={v.vehicleId}>{vehicleForm(() => setEditingId(null))}</div>
        ) : (
          <div key={v.vehicleId}>
            <div className="list-item">
              <div>
                <strong>{v.manufacturer}</strong> — {v.class}
                {v.radioManufacturer && <span className="muted"> · Radio: {v.radioManufacturer}</span>}
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="secondary" onClick={() => startEdit(v)}>Edit</button>
                <button className="secondary" onClick={() => startRegister(v)}>Register for race</button>
                <button className="danger" onClick={() => handleDelete(v.vehicleId)}>Delete</button>
              </div>
            </div>
            {registeringId === v.vehicleId && (
              <form onSubmit={handleRegister} className="row" style={{ marginBottom: "0.5rem" }}>
                <select value={regEventId} onChange={(e) => setRegEventId(e.target.value)} required>
                  <option value="" disabled>Select an event…</option>
                  {publishedEvents.map((ev) => (
                    <option key={ev.eventId} value={ev.eventId}>{ev.title}</option>
                  ))}
                </select>
                <input value={regClass} onChange={(e) => setRegClass(e.target.value)} placeholder="Class" required />
                <button type="submit" style={{ flex: "0 0 auto" }}>Generate checkout link</button>
                <button type="button" className="secondary" style={{ flex: "0 0 auto" }} onClick={() => setRegisteringId(null)}>Close</button>
              </form>
            )}
            {registeringId === v.vehicleId && regError && <p className="err">{regError}</p>}
            {registeringId === v.vehicleId && regResult && (
              <p className="ok" style={{ wordBreak: "break-all" }}>{regResult}</p>
            )}
          </div>
        ),
      )}
      {vehicles && vehicles.length === 0 && <p className="muted">No vehicles on file.</p>}
      {adding ? (
        vehicleForm(() => setAdding(false))
      ) : (
        <button type="button" className="secondary" style={{ marginTop: "0.5rem" }} onClick={startAdd}>
          + Add vehicle
        </button>
      )}
    </div>
  );
}

function UsersSection() {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [q, setQ] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [adminOnly, setAdminOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [fieldDefs, setFieldDefs] = useState<FieldDefinitionRow[]>([]);

  function refresh() {
    listAdminUsers(q || undefined)
      .then((res) => setUsers(res.users))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load users."));
  }
  useEffect(refresh, [q]);
  useEffect(() => {
    listFieldDefinitions("user").then((res) => setFieldDefs(res.fields)).catch(() => {});
  }, []);

  const filtered = (users ?? []).filter((u) => {
    if (verifiedOnly && !u.isVerifiedMember) return false;
    if (adminOnly && !u.isAdmin) return false;
    return true;
  });

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    try {
      const res = await updateAdminUser(editing.userId, {
        name: editing.name,
        phone: editing.phone,
        bio: editing.bio,
        isVerifiedMember: editing.isVerifiedMember,
        isAdmin: editing.isAdmin,
        customFields: editing.customFields,
      });
      setEditing(null);
      setUsers((prev) => prev?.map((u) => (u.userId === res.user.userId ? res.user : u)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  // Clears whatever's saved under this section's fields (see TODO #11 --
  // these are currently the vehicle-shaped fields, misscoped to
  // appliesTo:"user" instead of "vehicle") immediately, rather than waiting
  // for the form's own Save button, matching the Delete pattern used
  // elsewhere in this admin UI.
  async function handleClearCustomFields() {
    if (!editing) return;
    if (!confirm("Remove these saved values?")) return;
    const cleared = { ...editing.customFields };
    for (const f of fieldDefs) delete cleared[f.fieldKey];
    setError(null);
    try {
      const res = await updateAdminUser(editing.userId, { customFields: cleared });
      setEditing(res.user);
      setUsers((prev) => prev?.map((u) => (u.userId === res.user.userId ? res.user : u)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Remove failed.");
    }
  }

  return (
    <div>
      <h2>Users</h2>
      {error && <p className="err">{error}</p>}
      <div className="row filters">
        <input placeholder="Search by name/email…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label style={{ flex: "0 0 auto" }}>
          <input type="checkbox" style={{ width: "auto", marginRight: "0.4rem" }} checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />
          Verified only
        </label>
        <label style={{ flex: "0 0 auto" }}>
          <input type="checkbox" style={{ width: "auto", marginRight: "0.4rem" }} checked={adminOnly} onChange={(e) => setAdminOnly(e.target.checked)} />
          Admins only
        </label>
      </div>

      <div className="table-scroll"><table style={{ marginTop: "1rem" }}>
        <thead>
          <tr><th>Name</th><th>Email</th><th>Verified</th><th>Admin</th><th>Credits (all clubs)</th></tr>
        </thead>
        <tbody>
          {filtered.map((u) => (
            <tr key={u.userId} className="admin-user-row" onClick={() => setEditing(u)}>
              <td>{u.name}</td>
              <td>
                {u.authProvider === "facebook" ? (
                  <FacebookIcon className="muted" style={{ marginRight: 6, verticalAlign: "middle" }} />
                ) : (
                  <EnvelopeAtIcon className="muted" style={{ marginRight: 6, verticalAlign: "middle" }} />
                )}
                {u.email}
              </td>
              <td>{u.isVerifiedMember ? "Yes" : "No"}</td>
              <td>{u.isAdmin ? "Yes" : "No"}</td>
              <td>{formatDollars(u.creditBalanceCents)}</td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {editing && (
        <div className="card" style={{ marginTop: "1rem" }}>
        <form onSubmit={handleSave}>
          <h3>Edit {editing.email}</h3>
          <div className="row">
            <div>
              <label htmlFor="edit-name">Name</label>
              <input id="edit-name" value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="edit-phone">Phone</label>
              <input id="edit-phone" value={editing.phone ?? ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor="edit-bio">Bio</label>
            <textarea id="edit-bio" rows={3} value={editing.bio ?? ""} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.75rem" }}>
            <label style={{ flex: "0 0 auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto", marginRight: "0.4rem" }}
                checked={!!editing.isVerifiedMember}
                onChange={(e) => setEditing({ ...editing, isVerifiedMember: e.target.checked })}
              />
              Verified member
            </label>
            <label style={{ flex: "0 0 auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto", marginRight: "0.4rem" }}
                checked={!!editing.isAdmin}
                onChange={(e) => setEditing({ ...editing, isAdmin: e.target.checked })}
              />
              Admin
            </label>
          </div>

          {fieldDefs.length > 0 && (
            <div style={{ marginTop: "0.75rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <h4 style={{ margin: 0 }}>Add vehicle</h4>
                {fieldDefs.some((f) => {
                  const v = editing.customFields?.[f.fieldKey];
                  return v !== undefined && v !== "" && v !== false;
                }) && (
                  <button
                    type="button"
                    className="danger"
                    title="Remove saved values"
                    aria-label="Remove saved values"
                    style={{ padding: "0.3rem 0.4rem", display: "inline-flex" }}
                    onClick={handleClearCustomFields}
                  >
                    <Trash3FillIcon />
                  </button>
                )}
              </div>
              {fieldDefs.map((f) => (
                <div key={f.fieldKey} style={{ marginBottom: "0.5rem" }}>
                  <label htmlFor={`cf-${f.fieldKey}`}>{f.label}</label>
                  {f.type === "boolean" ? (
                    <input
                      id={`cf-${f.fieldKey}`}
                      type="checkbox"
                      style={{ width: "auto" }}
                      checked={!!editing.customFields?.[f.fieldKey]}
                      onChange={(e) =>
                        setEditing({ ...editing, customFields: { ...editing.customFields, [f.fieldKey]: e.target.checked } })
                      }
                    />
                  ) : f.type === "select" ? (
                    <select
                      id={`cf-${f.fieldKey}`}
                      value={String(editing.customFields?.[f.fieldKey] ?? "")}
                      onChange={(e) => setEditing({ ...editing, customFields: { ...editing.customFields, [f.fieldKey]: e.target.value } })}
                    >
                      <option value="" />
                      {(f.options ?? []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`cf-${f.fieldKey}`}
                      type={f.type === "number" ? "number" : "text"}
                      value={String(editing.customFields?.[f.fieldKey] ?? "")}
                      onChange={(e) => setEditing({ ...editing, customFields: { ...editing.customFields, [f.fieldKey]: e.target.value } })}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <button type="submit">Save</button>
            <button type="button" className="secondary" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>

        <UserVehiclesPanel userId={editing.userId} racerName={editing.name || editing.email} />
        </div>
      )}
    </div>
  );
}

function FieldDefinitionsSection() {
  const [appliesTo, setAppliesTo] = useState<FieldAppliesTo>("user");
  const [fields, setFields] = useState<FieldDefinitionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<FieldDefinitionRow>>({ appliesTo: "user", type: "text", required: false });
  const [editingKey, setEditingKey] = useState<string | null>(null);

  function refresh() {
    listFieldDefinitions(appliesTo)
      .then((res) => setFields(res.fields))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load fields."));
  }
  useEffect(refresh, [appliesTo]);

  function resetForm() {
    setEditingKey(null);
    setForm({ appliesTo, type: "text", required: false });
  }

  function startEdit(f: FieldDefinitionRow) {
    setEditingKey(f.fieldKey);
    setForm({ ...f });
  }

  // upsertFieldDefinition is a true upsert keyed on (appliesTo, fieldKey) --
  // submitting with an existing key overwrites that definition in place, so
  // Add and Save Changes are the same call; only the form's starting values
  // (blank vs. the row being edited) differ. fieldKey can't be changed once
  // created (that would make a second field, not rename this one), so it's
  // locked while editing.
  async function handleSubmit() {
    if (!form.fieldKey || !form.label) {
      setError("Key and Label are required.");
      return;
    }
    setError(null);
    try {
      await upsertFieldDefinition({
        ...form,
        appliesTo,
        options: form.type === "select" && typeof form.options === "string" ? (form.options as unknown as string).split(",").map((s) => s.trim()).filter(Boolean) : form.options,
      });
      resetForm();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    }
  }

  async function handleDelete(fieldKey: string) {
    if (!confirm(`Delete the "${fieldKey}" field? Any values already stored under it are orphaned, not removed.`)) return;
    try {
      await deleteFieldDefinition(appliesTo, fieldKey);
      if (editingKey === fieldKey) resetForm();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  return (
    <div>
      <h2>Field definitions</h2>
      <p className="muted" style={{ fontSize: 13 }}>
        Vehicle dropdowns: add a <strong>vehicle</strong> field of type <em>select</em> with key{" "}
        <code>manufacturer</code>, <code>class</code> or <code>radio-manufacturer</code> and comma-separated
        choices. Racers then pick from that list on My cars instead of typing.
      </p>
      {error && <p className="err">{error}</p>}
      <div className="row" style={{ marginBottom: "0.75rem" }}>
        <select value={appliesTo} onChange={(e) => { setAppliesTo(e.target.value as FieldAppliesTo); resetForm(); }}>
          <option value="user">user</option>
          <option value="vehicle">vehicle</option>
        </select>
      </div>
      <div className="table-scroll"><table>
        <thead><tr><th>Key</th><th>Label</th><th>Type</th><th>Required</th><th /></tr></thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.fieldKey}>
              <td>{f.fieldKey}</td>
              <td>{f.label}</td>
              <td>{f.type}{f.type === "select" && f.options ? ` (${f.options.join(", ")})` : ""}</td>
              <td>{f.required ? "Yes" : "No"}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="secondary" title="Edit" aria-label="Edit" style={{ padding: "0.3rem 0.4rem", display: "inline-flex" }} onClick={() => startEdit(f)}>
                  <PencilFillIcon />
                </button>
                <button
                  className="danger"
                  title="Delete"
                  aria-label="Delete"
                  style={{ padding: "0.3rem 0.4rem", display: "inline-flex", marginLeft: 8 }}
                  onClick={() => handleDelete(f.fieldKey)}
                >
                  <Trash3FillIcon />
                </button>
              </td>
            </tr>
          ))}
          {fields.length === 0 && <tr><td colSpan={5} className="muted">No fields defined for {appliesTo} yet.</td></tr>}

          {/* Add/edit row -- startEdit() above populates `form` from an
             existing row and this same row+button becomes the save action
             for it (upsertFieldDefinition is keyed on fieldKey, so Add and
             Save Changes are the same call either way). */}
          <tr>
            <td>
              <input
                aria-label="Key (slug)"
                placeholder="key_slug"
                value={form.fieldKey ?? ""}
                onChange={(e) => setForm({ ...form, fieldKey: e.target.value })}
                disabled={!!editingKey}
              />
            </td>
            <td>
              <input aria-label="Label" placeholder="Label" value={form.label ?? ""} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </td>
            <td>
              <select aria-label="Type" value={form.type ?? "text"} onChange={(e) => setForm({ ...form, type: e.target.value as FieldDefinitionRow["type"] })}>
                <option value="text">text</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
                <option value="select">select (single choice)</option>
              </select>
              {form.type === "select" && (
                <input
                  aria-label="Choices (comma separated)"
                  style={{ marginTop: "0.4rem" }}
                  value={typeof form.options === "string" ? form.options : (form.options ?? []).join(", ")}
                  onChange={(e) => setForm({ ...form, options: e.target.value as unknown as string[] })}
                  placeholder="Choices: Associated, Team Losi, …"
                />
              )}
            </td>
            <td>
              <input
                type="checkbox"
                aria-label="Required"
                style={{ width: "auto" }}
                checked={!!form.required}
                onChange={(e) => setForm({ ...form, required: e.target.checked })}
              />
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
              <button
                type="button"
                className="success"
                title={editingKey ? "Save changes" : "Add field"}
                aria-label={editingKey ? "Save changes" : "Add field"}
                style={{ padding: "0.3rem 0.6rem" }}
                onClick={handleSubmit}
              >
                {editingKey ? "✓" : "+"}
              </button>
              {editingKey && (
                <button type="button" className="secondary" title="Cancel" aria-label="Cancel" style={{ marginLeft: 8, padding: "0.3rem 0.6rem" }} onClick={resetForm}>
                  ✕
                </button>
              )}
            </td>
          </tr>
        </tbody>
      </table></div>
    </div>
  );
}

/** One qualifying or main heat: editable results table (manual entry, no
 *  auto-seeding) plus a Notify placeholder -- see the TODO comment below. */
function HeatCard({
  eventId,
  heat,
  registeredRacers,
  onChange,
  onDelete,
}: {
  eventId: string;
  heat: HeatRow;
  registeredRacers: string[];
  onChange: () => void;
  onDelete: () => void;
}) {
  const [results, setResults] = useState<HeatResult[]>(heat.results);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notified, setNotified] = useState(false);

  function updateRow(i: number, patch: Partial<HeatResult>) {
    setResults((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function removeRow(i: number) {
    setResults((prev) => prev.filter((_, idx) => idx !== i));
  }

  // One click instead of retyping every registered racer's name by hand --
  // only adds the ones not already listed, so it's safe to click again
  // after someone new registers mid-event.
  const missingRacers = registeredRacers.filter(
    (name) => !results.some((r) => r.racerName.trim().toLowerCase() === name.trim().toLowerCase()),
  );
  function addRegisteredRacers() {
    setResults((prev) => [...prev, ...missingRacers.map((racerName) => ({ racerName }))]);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await updateHeatResults(eventId, heat.heatId, results.filter((r) => r.racerName.trim().length > 0));
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const label = heat.roundType === "qualifier" ? `Qualifier ${heat.roundNumber}` : `Main ${heat.roundNumber}`;

  return (
    <div className="card" style={{ marginTop: "0.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ margin: 0 }}>{label}</h4>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {/* TODO: actually notify racers (push/SMS/email, whichever channel
             we land on) that their heat is coming up -- this button is a
             placeholder only, it doesn't send anything yet. */}
          <button type="button" className="secondary" onClick={() => setNotified(true)}>
            Notify racers
          </button>
          <button type="button" className="danger" onClick={onDelete}>Delete heat</button>
        </div>
      </div>
      {notified && (
        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Notifications aren't wired up yet -- this button is a placeholder.
        </p>
      )}
      {error && <p className="err">{error}</p>}
      <div className="table-scroll"><table style={{ marginTop: "0.5rem" }}>
        <thead>
          <tr><th>Racer</th><th>Place</th><th>Laps</th><th>Best lap</th><th></th></tr>
        </thead>
        <tbody>
          {results.map((r, i) => (
            <tr key={i}>
              <td>
                <input value={r.racerName} onChange={(e) => updateRow(i, { racerName: e.target.value })} placeholder="Racer name" />
              </td>
              <td>
                <input
                  type="number"
                  min={1}
                  value={r.place ?? ""}
                  onChange={(e) => updateRow(i, { place: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  style={{ maxWidth: 70 }}
                />
              </td>
              <td>
                <input
                  type="number"
                  min={0}
                  value={r.laps ?? ""}
                  onChange={(e) => updateRow(i, { laps: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  style={{ maxWidth: 70 }}
                />
              </td>
              <td>
                <input
                  value={r.bestLapTime ?? ""}
                  onChange={(e) => updateRow(i, { bestLapTime: e.target.value })}
                  placeholder="1:23.456"
                  style={{ maxWidth: 100 }}
                />
              </td>
              <td>
                <button type="button" className="danger" onClick={() => removeRow(i)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        {missingRacers.length > 0 && (
          <button type="button" className="secondary" onClick={addRegisteredRacers}>
            + Add {missingRacers.length} registered racer{missingRacers.length === 1 ? "" : "s"}
          </button>
        )}
        <button type="button" className="secondary" onClick={() => setResults((prev) => [...prev, { racerName: "" }])}>
          + Add racer result
        </button>
        <button type="button" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save results"}
        </button>
      </div>
    </div>
  );
}

/** Race-day management for one event: per-class registered-racer list,
 *  qualifying/main heats, and manual results entry. Heat counts from the
 *  event's class config are just a hint -- admins add heats one at a time
 *  with the buttons below, nothing here auto-generates or auto-seeds them. */
function RaceManagePanel({ event }: { event: EventRow }) {
  const [heats, setHeats] = useState<HeatRow[] | null>(null);
  const [registrations, setRegistrations] = useState<RegistrationRow[] | null>(null);
  const [selectedClass, setSelectedClass] = useState(event.classes?.[0]?.name ?? "");
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    listHeats(event.eventId)
      .then((res) => setHeats(res.heats))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load heats."));
    listEventRegistrations(event.eventId)
      .then((res) => setRegistrations(res.registrations))
      .catch(() => {});
  }
  useEffect(refresh, [event.eventId]);

  const classes = event.classes ?? [];
  const classConfig = classes.find((c) => c.name === selectedClass);
  const classRegistrations = (registrations ?? []).filter((r) => r.racerClass === selectedClass);
  const classHeats = (heats ?? [])
    .filter((h) => h.className === selectedClass)
    .sort((a, b) => (a.roundType === b.roundType ? a.roundNumber - b.roundNumber : a.roundType === "qualifier" ? -1 : 1));

  async function addHeat(roundType: HeatRoundType) {
    const existingCount = classHeats.filter((h) => h.roundType === roundType).length;
    setError(null);
    try {
      await createHeat(event.eventId, { className: selectedClass, roundType, roundNumber: existingCount + 1 });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add heat.");
    }
  }

  async function handleDeleteHeat(heatId: string) {
    if (!confirm("Delete this heat and its results?")) return;
    try {
      await deleteHeat(event.eventId, heatId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  // Manual override for when a registration will never get a Stripe
  // webhook on its own -- e.g. someone pays cash at the track.
  async function handleMarkPaid(registrationId: string) {
    setError(null);
    try {
      await updateRegistrationStatus(event.eventId, registrationId, "paid");
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to mark paid.");
    }
  }

  // Cancelling gives back any credits taken at checkout -- including ones
  // held because Stripe was unavailable when they checked out.
  async function handleCancel(r: RegistrationRow) {
    const credits = r.creditsAppliedCents > 0 && !r.creditsReleasedAt;
    const msg = credits
      ? `Cancel ${r.racerName}'s registration and return $${(r.creditsAppliedCents / 100).toFixed(2)} in credits?`
      : `Cancel ${r.racerName}'s registration?`;
    if (!confirm(msg)) return;
    setError(null);
    try {
      await updateRegistrationStatus(event.eventId, r.registrationId, "cancelled");
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel.");
    }
  }

  if (classes.length === 0) {
    return (
      <div className="card" style={{ marginTop: "0.75rem" }}>
        <p className="muted">This event has no classes configured yet -- add one in Edit above.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: "0.75rem" }}>
      <h3>Manage {event.title}</h3>
      {error && <p className="err">{error}</p>}
      {classes.length > 1 && (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
          {classes.map((c) => (
            <button
              type="button"
              key={c.name}
              className={c.name === selectedClass ? "" : "secondary"}
              onClick={() => setSelectedClass(c.name)}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      {classConfig && (
        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Configured for {classConfig.qualifyingRounds} qualifier{classConfig.qualifyingRounds === 1 ? "" : "s"} and{" "}
          {classConfig.mains} main{classConfig.mains === 1 ? "" : "s"}.
        </p>
      )}
      <h4>Registered for {selectedClass}</h4>
      {classRegistrations.length === 0 ? (
        <p className="muted">No one registered yet.</p>
      ) : (
        <ul>
          {classRegistrations.map((r) => (
            <li key={r.registrationId}>
              {r.racerName} <span className="muted">({r.status})</span>
              {r.status === "pending" && r.paymentHeldAt && (
                <span className="muted" style={{ marginLeft: 6, fontSize: "0.8rem" }}>
                  payment unavailable at checkout
                  {r.creditsAppliedCents > 0 ? `, $${(r.creditsAppliedCents / 100).toFixed(2)} credits held` : ""}
                </span>
              )}
              {r.status === "pending" && (
                <>
                  <button
                    type="button"
                    className="success"
                    style={{ marginLeft: 8, padding: "0.15rem 0.5rem", fontSize: "0.8rem" }}
                    onClick={() => handleMarkPaid(r.registrationId)}
                  >
                    Mark paid
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    style={{ marginLeft: 4, padding: "0.15rem 0.5rem", fontSize: "0.8rem" }}
                    onClick={() => handleCancel(r)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <h4 style={{ marginTop: "1rem" }}>Heats</h4>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button type="button" className="secondary" onClick={() => addHeat("qualifier")}>+ Add qualifying heat</button>
        <button type="button" className="secondary" onClick={() => addHeat("main")}>+ Add main</button>
      </div>
      {classHeats.map((h) => (
        <HeatCard
          key={h.heatId}
          eventId={event.eventId}
          heat={h}
          registeredRacers={classRegistrations.filter((r) => r.status !== "cancelled" && r.status !== "refunded").map((r) => r.racerName)}
          onChange={refresh}
          onDelete={() => handleDeleteHeat(h.heatId)}
        />
      ))}
      {classHeats.length === 0 && <p className="muted" style={{ marginTop: "0.5rem" }}>No heats added yet.</p>}
    </div>
  );
}

function EventsSection() {
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [trackLocations, setTrackLocations] = useState<LocationRow[]>([]);
  const [clubs, setClubs] = useState<ClubRow[]>([]);
  const classOptions = useClassOptions();
  const [createForm, setCreateForm] = useState<Partial<EventRow>>({ status: "draft", classes: [] });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<EventRow>>({});
  const [managingId, setManagingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Location is a dropdown of the admin-managed Track list (see
  // LocationsByType below) rather than free text, so event postings always
  // point at a real, consistently-named track.
  useEffect(() => {
    listAdminLocations("track").then((res) => setTrackLocations(res.locations)).catch(() => {});
    listAdminClubs().then((res) => setClubs(res.clubs)).catch(() => {});
  }, []);

  function refresh() {
    listAdminEvents()
      .then((res) => setEvents(res.events))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load events."));
  }
  useEffect(refresh, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    try {
      // New featured events append to the end of the featured list --
      // reordering from there happens via the up/down arrows in the table.
      const featuredOrder = createForm.featured ? (events?.filter((ev) => ev.featured).length ?? 0) : undefined;
      const res = await createAdminEvent({ ...cleanEventForm(createForm, false), featuredOrder });
      setInfo(`Created "${res.event.title}".`);
      setCreateForm({ status: "draft", classes: [] });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed.");
    }
  }

  function startEdit(ev: EventRow) {
    setEditingId(ev.eventId);
    setEditForm({ ...ev });
    setInfo(null);
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError(null);
    setInfo(null);
    try {
      // Newly-checked "Featured" with no order yet (just toggled on in this
      // form) appends to the end; an already-featured event keeps whatever
      // featuredOrder it already had.
      const featuredOrder =
        editForm.featured && editForm.featuredOrder === undefined
          ? (events?.filter((ev) => ev.featured && ev.eventId !== editingId).length ?? 0)
          : editForm.featuredOrder;
      const res = await updateAdminEvent(editingId, {
        ...cleanEventForm(editForm, true),
        featuredOrder,
        // "No club" can't be saved back (the API rejects a blank clubId) --
        // leaving it unset just means "don't change it".
        clubId: editForm.clubId || undefined,
      });
      setInfo(`Updated "${res.event.title}".`);
      setEditingId(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed.");
    }
  }

  async function handleDelete(ev: EventRow) {
    if (!confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try {
      await deleteAdminEvent(ev.eventId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  // Swap featuredOrder with the neighboring featured event -- same
  // two-PUT swap pattern as LocationsByType's move() below.
  async function moveFeatured(ev: EventRow, direction: -1 | 1) {
    if (!events) return;
    const featuredList = events.filter((e) => e.featured).sort((a, b) => (a.featuredOrder ?? 0) - (b.featuredOrder ?? 0));
    const idx = featuredList.findIndex((e) => e.eventId === ev.eventId);
    const otherIdx = idx + direction;
    if (idx < 0 || otherIdx < 0 || otherIdx >= featuredList.length) return;
    const a = featuredList[idx];
    const b = featuredList[otherIdx];
    setError(null);
    try {
      await Promise.all([
        updateAdminEvent(a.eventId, { featuredOrder: b.featuredOrder ?? 0 }),
        updateAdminEvent(b.eventId, { featuredOrder: a.featuredOrder ?? 0 }),
      ]);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed.");
    }
  }

  const featuredEvents = events?.filter((e) => e.featured).sort((a, b) => (a.featuredOrder ?? 0) - (b.featuredOrder ?? 0)) ?? [];

  return (
    <div>
      <h2>Events</h2>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <EventForm
        idPrefix="ce"
        heading="Create event"
        submitLabel="Create event"
        editing={false}
        value={createForm}
        onChange={setCreateForm}
        onSubmit={handleCreate}
        trackNames={trackLocations.map((l) => l.name)}
        classOptions={classOptions}
        clubs={clubs}
        showFeatured
      />

      <div className="table-scroll"><table style={{ marginTop: "1rem" }}>
        <thead><tr><th></th><th>Title</th><th>Date</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {events?.map((ev) => {
            const featuredIdx = ev.featured ? featuredEvents.findIndex((e) => e.eventId === ev.eventId) : -1;
            return (
              <tr key={ev.eventId}>
                <td style={{ whiteSpace: "nowrap" }}>
                  {ev.featured && (
                    <>
                      <button className="secondary" disabled={featuredIdx === 0} onClick={() => moveFeatured(ev, -1)}>↑</button>
                      <button className="secondary" disabled={featuredIdx === featuredEvents.length - 1} style={{ marginLeft: 4 }} onClick={() => moveFeatured(ev, 1)}>↓</button>
                    </>
                  )}
                </td>
                <td>{ev.title}{ev.featured && <span className="pill success" style={{ marginLeft: 6 }}>Featured</span>}</td>
                <td className="muted">{formatEventDate(ev.dateISO, ev.endDateISO)}</td>
                <td><span className={`pill ${ev.status === "published" ? "success" : "warn"}`}>{ev.status}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary" onClick={() => setManagingId(managingId === ev.eventId ? null : ev.eventId)}>Manage</button>
                  <button className="secondary" style={{ marginLeft: 8 }} onClick={() => startEdit(ev)}>Edit</button>
                  <button className="danger" style={{ marginLeft: 8 }} onClick={() => handleDelete(ev)}>Delete</button>
                </td>
              </tr>
            );
          })}
          {events && events.length === 0 && <tr><td colSpan={5} className="muted">No events yet.</td></tr>}
        </tbody>
      </table></div>

      {managingId && (() => {
        const managingEvent = events?.find((e) => e.eventId === managingId);
        return managingEvent ? <RaceManagePanel event={managingEvent} /> : null;
      })()}

      {editingId && (
        <EventForm
          idPrefix="ee"
          heading="Edit event"
          submitLabel="Save changes"
          editing
          value={editForm}
          onChange={setEditForm}
          onSubmit={handleUpdate}
          onCancel={() => setEditingId(null)}
          trackNames={trackLocations.map((l) => l.name)}
          classOptions={classOptions}
          clubs={clubs}
          showFeatured
          style={{ marginTop: "0.75rem" }}
        />
      )}
    </div>
  );
}

function ArticlesSection() {
  const [articles, setArticles] = useState<ArticleRow[] | null>(null);
  const [createForm, setCreateForm] = useState<Partial<ArticleRow>>({
    status: "draft",
    publishDate: new Date().toISOString().slice(0, 10),
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ArticleRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function refresh() {
    listAdminArticles()
      .then((res) => setArticles(res.articles))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load articles."));
  }
  useEffect(refresh, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    try {
      // New featured articles append to the end of the featured list --
      // reordering from there happens via the up/down arrows in the table.
      const featuredOrder = createForm.featured ? (articles?.filter((a) => a.featured).length ?? 0) : undefined;
      const res = await createAdminArticle({ ...createForm, featuredOrder });
      setInfo(`Created "${res.article.title}".`);
      setCreateForm({ status: "draft", publishDate: new Date().toISOString().slice(0, 10) });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed.");
    }
  }

  function startEdit(a: ArticleRow) {
    setEditingId(a.articleId);
    setEditForm({ ...a });
    setInfo(null);
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError(null);
    setInfo(null);
    try {
      // Newly-checked "Featured" with no order yet (just toggled on in this
      // form) appends to the end; an already-featured article keeps
      // whatever featuredOrder it already had.
      const featuredOrder =
        editForm.featured && editForm.featuredOrder === undefined
          ? (articles?.filter((a) => a.featured && a.articleId !== editingId).length ?? 0)
          : editForm.featuredOrder;
      const res = await updateAdminArticle(editingId, { ...editForm, featuredOrder });
      setInfo(`Updated "${res.article.title}".`);
      setEditingId(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed.");
    }
  }

  async function handleDelete(a: ArticleRow) {
    if (!confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await deleteAdminArticle(a.articleId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  // Swap featuredOrder with the neighboring featured article -- same
  // two-PUT swap pattern as LocationsByType's move() below.
  async function moveFeatured(a: ArticleRow, direction: -1 | 1) {
    if (!articles) return;
    const featuredList = articles.filter((x) => x.featured).sort((x, y) => (x.featuredOrder ?? 0) - (y.featuredOrder ?? 0));
    const idx = featuredList.findIndex((x) => x.articleId === a.articleId);
    const otherIdx = idx + direction;
    if (idx < 0 || otherIdx < 0 || otherIdx >= featuredList.length) return;
    const x = featuredList[idx];
    const y = featuredList[otherIdx];
    setError(null);
    try {
      await Promise.all([
        updateAdminArticle(x.articleId, { featuredOrder: y.featuredOrder ?? 0 }),
        updateAdminArticle(y.articleId, { featuredOrder: x.featuredOrder ?? 0 }),
      ]);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed.");
    }
  }

  const featuredArticles = articles?.filter((a) => a.featured).sort((x, y) => (x.featuredOrder ?? 0) - (y.featuredOrder ?? 0)) ?? [];

  return (
    <div>
      <h2>Information</h2>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleCreate} className="card">
        <h3>Create article</h3>
        <label htmlFor="ca-title">Title</label>
        <input id="ca-title" value={createForm.title ?? ""} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} required />
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="ca-summary">Summary (optional)</label>
          <input id="ca-summary" value={createForm.summary ?? ""} onChange={(e) => setCreateForm({ ...createForm, summary: e.target.value })} />
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="ca-body">Body</label>
          <RichTextEditor key="create" initialValue={createForm.body ?? ""} onChange={(html) => setCreateForm((f) => ({ ...f, body: html }))} />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor="ca-status">Status</label>
            <select id="ca-status" value={createForm.status ?? "draft"} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value as ArticleRow["status"] })}>
              <option value="draft">draft</option>
              <option value="published">published</option>
            </select>
          </div>
          <div>
            <label htmlFor="ca-publishdate">Publish date</label>
            <input
              id="ca-publishdate"
              type="date"
              value={createForm.publishDate ?? ""}
              onChange={(e) => setCreateForm({ ...createForm, publishDate: e.target.value })}
            />
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.75rem" }}>
          <input type="checkbox" style={{ width: "auto" }} checked={!!createForm.featured} onChange={(e) => setCreateForm({ ...createForm, featured: e.target.checked })} />
          Featured on front page
        </label>
        <button type="submit" style={{ marginTop: "0.75rem" }}>Create article</button>
      </form>

      <div className="table-scroll"><table style={{ marginTop: "1rem" }}>
        <thead><tr><th></th><th>Title</th><th>Posted</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {articles?.map((a) => {
            const featuredIdx = a.featured ? featuredArticles.findIndex((x) => x.articleId === a.articleId) : -1;
            return (
              <tr key={a.articleId}>
                <td style={{ whiteSpace: "nowrap" }}>
                  {a.featured && (
                    <>
                      <button className="secondary" disabled={featuredIdx === 0} onClick={() => moveFeatured(a, -1)}>↑</button>
                      <button className="secondary" disabled={featuredIdx === featuredArticles.length - 1} style={{ marginLeft: 4 }} onClick={() => moveFeatured(a, 1)}>↓</button>
                    </>
                  )}
                </td>
                <td>{a.title}{a.featured && <span className="pill success" style={{ marginLeft: 6 }}>Featured</span>}</td>
                <td className="muted">{new Date(a.publishDate || a.createdAt).toLocaleDateString()}</td>
                <td><span className={`pill ${a.status === "published" ? "success" : "warn"}`}>{a.status}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary" onClick={() => startEdit(a)}>Edit</button>
                  <button className="danger" style={{ marginLeft: 8 }} onClick={() => handleDelete(a)}>Delete</button>
                </td>
              </tr>
            );
          })}
          {articles && articles.length === 0 && <tr><td colSpan={5} className="muted">No articles yet.</td></tr>}
        </tbody>
      </table></div>

      {editingId && (
        <form onSubmit={handleUpdate} className="card" style={{ marginTop: "0.75rem" }}>
          <h3>Edit article</h3>
          <label htmlFor="ea-title">Title</label>
          <input id="ea-title" value={editForm.title ?? ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} required />
          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor="ea-summary">Summary</label>
            <input id="ea-summary" value={editForm.summary ?? ""} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })} />
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor="ea-body">Body</label>
            <RichTextEditor key={editingId} initialValue={editForm.body ?? ""} onChange={(html) => setEditForm((f) => ({ ...f, body: html }))} />
          </div>
          <div className="row" style={{ marginTop: "0.75rem" }}>
            <div>
              <label htmlFor="ea-status">Status</label>
              <select id="ea-status" value={editForm.status ?? "draft"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as ArticleRow["status"] })}>
                <option value="draft">draft</option>
                <option value="published">published</option>
              </select>
            </div>
            <div>
              <label htmlFor="ea-publishdate">Publish date</label>
              <input
                id="ea-publishdate"
                type="date"
                value={editForm.publishDate ?? ""}
                onChange={(e) => setEditForm({ ...editForm, publishDate: e.target.value })}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <button type="submit">Save changes</button>
            <button type="button" className="secondary" onClick={() => setEditingId(null)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Renders one type's (Track or Hobby Shop) full admin CRUD + reorder UI.
 *  Both instances share the same LocationsTable/endpoints (scoped by
 *  `type`) -- this is purely two separately-headed views onto it, per the
 *  user's ask for a distinct "Hobby Shop section" rather than one section
 *  with a type toggle. Form ids are prefixed with `type` since both
 *  instances render in the DOM at once. */
function LocationsByType({ type, title }: { type: LocationType; title: string }) {
  const [locations, setLocations] = useState<LocationRow[] | null>(null);
  const [createForm, setCreateForm] = useState<Partial<LocationRow>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<LocationRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  function refresh() {
    listAdminLocations(type)
      .then((res) => setLocations(res.locations))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load locations."));
  }
  useEffect(refresh, [type]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    try {
      const res = await createAdminLocation({ ...createForm, type });
      setInfo(`Added "${res.location.name}".`);
      setCreateForm({});
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed.");
    }
  }

  function startEdit(l: LocationRow) {
    setEditingId(l.locationId);
    setEditForm({ ...l });
    setInfo(null);
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError(null);
    setInfo(null);
    try {
      const res = await updateAdminLocation(editingId, editForm);
      setInfo(`Updated "${res.location.name}".`);
      setEditingId(null);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed.");
    }
  }

  async function handleDelete(l: LocationRow) {
    if (!confirm(`Delete "${l.name}"? This cannot be undone.`)) return;
    try {
      await deleteAdminLocation(l.locationId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editingId) return;
    setError(null);
    setUploadingPhoto(true);
    try {
      const { uploadUrl, s3Key } = await getLocationPhotoUploadUrl(editingId, file.name, file.type);
      const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!putRes.ok) throw new Error("Upload to storage failed.");
      const photoKeys = [...(editForm.photoKeys ?? []), s3Key];
      const res = await updateAdminLocation(editingId, { photoKeys });
      // updateAdminLocation returns the raw row -- photoUrls is only
      // computed by the list endpoints -- so build it here too rather than
      // showing a blank gallery until the next refresh() repopulates it.
      setEditForm({ ...res.location, photoUrls: photoKeys.map((k) => `${window.location.origin}/media/${k}`) });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Photo upload failed.");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  }

  async function removePhoto(key: string) {
    if (!editingId) return;
    setError(null);
    try {
      const photoKeys = (editForm.photoKeys ?? []).filter((k) => k !== key);
      const res = await updateAdminLocation(editingId, { photoKeys });
      setEditForm({ ...res.location, photoUrls: photoKeys.map((k) => `${window.location.origin}/media/${k}`) });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Remove photo failed.");
    }
  }

  // Swap this row's `order` with its neighbor's -- two PUTs, since the
  // backend has no dedicated reorder endpoint (order is just a field on the
  // same whitelist updateLocation already supports).
  async function move(index: number, direction: -1 | 1) {
    if (!locations) return;
    const otherIndex = index + direction;
    if (otherIndex < 0 || otherIndex >= locations.length) return;
    const a = locations[index];
    const b = locations[otherIndex];
    setError(null);
    try {
      await Promise.all([
        updateAdminLocation(a.locationId, { order: b.order }),
        updateAdminLocation(b.locationId, { order: a.order }),
      ]);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed.");
    }
  }

  const typeLabel = type === "track" ? "track" : "hobby shop";

  return (
    <div>
      <h2>{title}</h2>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleCreate} className="card">
        <h3>Add {typeLabel}</h3>
        <label htmlFor={`cl-name-${type}`}>Name</label>
        <input id={`cl-name-${type}`} value={createForm.name ?? ""} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required />
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor={`cl-address-${type}`}>Address</label>
          <input id={`cl-address-${type}`} value={createForm.address ?? ""} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor={`cl-phone-${type}`}>Phone</label>
            <input id={`cl-phone-${type}`} value={createForm.phone ?? ""} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
          </div>
          <div>
            <label htmlFor={`cl-website-${type}`}>Website</label>
            <input id={`cl-website-${type}`} value={createForm.website ?? ""} onChange={(e) => setCreateForm({ ...createForm, website: e.target.value })} />
          </div>
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor={`cl-description-${type}`}>Description</label>
          <textarea id={`cl-description-${type}`} rows={3} value={createForm.description ?? ""} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor={`cl-manager-email-${type}`}>Manager email</label>
          <input id={`cl-manager-email-${type}`} type="email" value={createForm.managerEmail ?? ""} onChange={(e) => setCreateForm({ ...createForm, managerEmail: e.target.value })} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.75rem" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={createForm.published !== false}
            onChange={(e) => setCreateForm({ ...createForm, published: e.target.checked })}
          />
          Published (visible on the site)
        </label>
        <button type="submit" style={{ marginTop: "0.75rem" }}>Add {typeLabel}</button>
      </form>

      <div className="table-scroll"><table style={{ marginTop: "1rem" }}>
        <thead><tr><th></th><th>Name</th><th>Address</th><th></th></tr></thead>
        <tbody>
          {locations?.map((l, i) => (
            <tr key={l.locationId}>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="secondary" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button className="secondary" disabled={i === locations.length - 1} style={{ marginLeft: 4 }} onClick={() => move(i, 1)}>↓</button>
              </td>
              <td>
                {l.name}
                {l.published === false && <span className="pill warn" style={{ marginLeft: 6 }}>Hidden</span>}
              </td>
              <td className="muted">{l.address}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="secondary" onClick={() => startEdit(l)}>Edit</button>
                <button className="danger" style={{ marginLeft: 8 }} onClick={() => handleDelete(l)}>Delete</button>
              </td>
            </tr>
          ))}
          {locations && locations.length === 0 && (
            <tr><td colSpan={4} className="muted">No {type === "track" ? "tracks" : "hobby shops"} yet.</td></tr>
          )}
        </tbody>
      </table></div>

      {editingId && (
        <form onSubmit={handleUpdate} className="card" style={{ marginTop: "0.75rem" }}>
          <h3>Edit {typeLabel}</h3>
          <label htmlFor={`el-name-${type}`}>Name</label>
          <input id={`el-name-${type}`} value={editForm.name ?? ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor={`el-address-${type}`}>Address</label>
            <input id={`el-address-${type}`} value={editForm.address ?? ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
          </div>
          <div className="row" style={{ marginTop: "0.75rem" }}>
            <div>
              <label htmlFor={`el-phone-${type}`}>Phone</label>
              <input id={`el-phone-${type}`} value={editForm.phone ?? ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div>
              <label htmlFor={`el-website-${type}`}>Website</label>
              <input id={`el-website-${type}`} value={editForm.website ?? ""} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor={`el-description-${type}`}>Description</label>
            <textarea id={`el-description-${type}`} rows={3} value={editForm.description ?? ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor={`el-manager-email-${type}`}>Manager email</label>
            <input id={`el-manager-email-${type}`} type="email" value={editForm.managerEmail ?? ""} onChange={(e) => setEditForm({ ...editForm, managerEmail: e.target.value })} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.75rem" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={editForm.published !== false}
              onChange={(e) => setEditForm({ ...editForm, published: e.target.checked })}
            />
            Published (visible on the site)
          </label>

          <div style={{ marginTop: "0.75rem" }}>
            <label htmlFor={`el-photo-${type}`}>Photos</label>
            <input id={`el-photo-${type}`} type="file" accept="image/*" ref={photoInputRef} onChange={handlePhotoChange} disabled={uploadingPhoto} />
            {uploadingPhoto && <p className="muted">Uploading…</p>}
            {editForm.photoUrls && editForm.photoUrls.length > 0 && (
              <div className="media-grid" style={{ marginTop: "0.5rem" }}>
                {editForm.photoUrls.map((url, i) => (
                  <div className="media-item" key={url}>
                    <img src={url} alt="" />
                    <button
                      type="button"
                      className="danger media-delete"
                      onClick={() => removePhoto((editForm.photoKeys ?? [])[i])}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
            <button type="submit">Save changes</button>
            <button type="button" className="secondary" onClick={() => setEditingId(null)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

function RegistrationsSection() {
  const [eventId, setEventId] = useState("");
  const [registrations, setRegistrations] = useState<RegistrationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleLoad(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await listEventRegistrations(eventId);
      setRegistrations(res.registrations);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load registrations.");
    }
  }

  return (
    <div>
      <h2>Registrations</h2>
      {error && <p className="err">{error}</p>}
      <form onSubmit={handleLoad} className="row">
        <input placeholder="Event ID" value={eventId} onChange={(e) => setEventId(e.target.value)} required />
        <button type="submit" style={{ flex: "0 0 auto" }}>Load</button>
      </form>
      {registrations && (
        <div className="table-scroll"><table style={{ marginTop: "0.75rem" }}>
          <thead><tr><th>Racer</th><th>Class</th><th>Status</th><th>Amount</th><th>Credits applied</th></tr></thead>
          <tbody>
            {registrations.map((r) => (
              <tr key={r.registrationId}>
                <td>{r.racerName}</td>
                <td>{r.racerClass}</td>
                <td>{r.status}</td>
                <td>{formatDollars(r.amountCents)}</td>
                <td>{formatDollars(r.creditsAppliedCents)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function AwardCreditSection() {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<UserRow[]>([]);
  const [selected, setSelected] = useState<UserRow | null>(null);
  const [amountDollars, setAmountDollars] = useState("");
  const [reason, setReason] = useState("");
  // Credits belong to one club and can only be spent on its events.
  const [clubs, setClubs] = useState<ClubRow[]>([]);
  const [clubId, setClubId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    listAdminClubs()
      .then((res) => {
        setClubs(res.clubs);
        if (res.clubs.length === 1) setClubId(res.clubs[0].clubId);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selected || query.trim().length < 2) {
      setMatches([]);
      return;
    }
    listAdminUsers(query)
      .then((res) => setMatches(res.users))
      .catch(() => setMatches([]));
  }, [query, selected]);

  function pick(u: UserRow) {
    setSelected(u);
    setQuery("");
    setMatches([]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setError(null);
    setInfo(null);
    try {
      await awardCredit({
        userId: selected.userId,
        clubId,
        amountCents: Math.round(parseFloat(amountDollars || "0") * 100),
        reason,
      });
      const clubName = clubs.find((c) => c.clubId === clubId)?.name ?? "the club";
      setInfo(`Credit awarded to ${selected.name || selected.email} at ${clubName}.`);
      setSelected(null);
      setAmountDollars("");
      setReason("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Award failed.");
    }
  }

  return (
    <div>
      <h2>Award credit</h2>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}
      <form onSubmit={handleSubmit} className="card">
        <label htmlFor="ac-search">Racer</label>
        {selected ? (
          <div className="row" style={{ alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <strong>{selected.name || "(no name on file)"}</strong>
              <div className="muted" style={{ fontSize: "0.85rem" }}>
                {selected.email}
                {selected.phone ? ` · ${selected.phone}` : ""}
              </div>
            </div>
            <button type="button" className="secondary" style={{ flex: "0 0 auto" }} onClick={() => setSelected(null)}>
              Change
            </button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <input
              id="ac-search"
              placeholder="Search by name, email, or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
            {matches.length > 0 && (
              <div className="account-dropdown" style={{ left: 0, right: 0, minWidth: 0 }}>
                {matches.map((u) => (
                  <button type="button" key={u.userId} onClick={() => pick(u)}>
                    <div style={{ textAlign: "left" }}>
                      <div>{u.name || "(no name on file)"}</div>
                      <div className="muted" style={{ fontSize: "0.85rem" }}>
                        {u.email}
                        {u.phone ? ` · ${u.phone}` : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor="ac-club">Club</label>
            <select id="ac-club" value={clubId} onChange={(e) => setClubId(e.target.value)} required>
              <option value="" disabled>Pick a club…</option>
              {clubs.map((c) => (
                <option key={c.clubId} value={c.clubId}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ac-amount">Amount ($)</label>
            <input id="ac-amount" type="number" step="0.01" value={amountDollars} onChange={(e) => setAmountDollars(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="ac-reason">Reason</label>
            <input id="ac-reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
          </div>
        </div>
        <button type="submit" style={{ marginTop: "0.75rem" }} disabled={!selected || !clubId}>Award credit</button>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();

  if (!user?.isAdmin) {
    return <p className="notice">Not authorized.</p>;
  }

  return (
    <div className="admin-sections">
      <h1>Admin</h1>
      <UsersSection />
      <ClubsAdminSection />
      <FieldDefinitionsSection />
      <EventsSection />
      <ArticlesSection />
      <LocationsByType type="track" title="Tracks" />
      <LocationsByType type="hobby_shop" title="Hobby Shops" />
      <RegistrationsSection />
      <AwardCreditSection />
    </div>
  );
}
